/**
 * Camera Form Component
 *
 * Modal form for creating/editing camera configurations.
 * Includes all capture schedule settings and Alta deployment options.
 */

import { useState, useEffect } from 'react';
import { X, Camera, Loader2, Settings2, AlertCircle } from 'lucide-react';
import { useCreateCamera, useUpdateCamera, useAvailableCameras, type CameraConfiguration, type CreateCamera } from '../../hooks/useCameras';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useStationLookup } from '../../hooks/useStations';
import { useSopLookup } from '../../hooks/useSOPs';

interface CameraFormProps {
  camera: CameraConfiguration | null;
  onClose: () => void;
}

export function CameraForm({ camera, onClose }: CameraFormProps) {
  const isEdit = !!camera;

  // Form state
  const [cameraId, setCameraId] = useState(camera?.cameraId ?? '');
  const [name, setName] = useState(camera?.name ?? '');
  const [location, setLocation] = useState(camera?.location ?? '');
  const [departmentId, setDepartmentId] = useState(camera?.departmentId ?? '');
  const [stationId, setStationId] = useState(camera?.stationId ?? '');
  const [selectedSopIds, setSelectedSopIds] = useState<string[]>(camera?.sops?.map(s => s.id) ?? []);
  const [captureEnabled, setCaptureEnabled] = useState(camera?.captureEnabled ?? true);
  // Phase 34 (F1): lens count for multi-sensor cameras
  const [sourceCount, setSourceCount] = useState<number>(camera?.sourceCount ?? 1);
  // Phase 11: Removed captureStartHour/captureEndHour - now 24/7 operation
  const [altaDeploymentName, setAltaDeploymentName] = useState(camera?.altaDeploymentName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [useManualInput, setUseManualInput] = useState(false);

  // Lookup data
  const { data: departments } = useDepartmentLookup();
  const { data: stations } = useStationLookup();
  const { data: sops } = useSopLookup();
  const { data: availableCameras, isLoading: camerasLoading } = useAvailableCameras();

  // Mutations
  const createMutation = useCreateCamera();
  const updateMutation = useUpdateCamera();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when camera changes
  useEffect(() => {
    setCameraId(camera?.cameraId ?? '');
    setName(camera?.name ?? '');
    setLocation(camera?.location ?? '');
    setDepartmentId(camera?.departmentId ?? '');
    setStationId(camera?.stationId ?? '');
    setSelectedSopIds(camera?.sops?.map(s => s.id) ?? []);
    setCaptureEnabled(camera?.captureEnabled ?? true);
    setSourceCount(camera?.sourceCount ?? 1); // Phase 34
    // Phase 11: Removed captureStartHour/captureEndHour - now 24/7 operation
    setAltaDeploymentName(camera?.altaDeploymentName ?? '');
    setError(null);
    setUseManualInput(false);
  }, [camera]);

  // Handle camera selection from dropdown
  const handleCameraSelect = (selectedId: string) => {
    setCameraId(selectedId);
    const selectedCamera = availableCameras?.find(c => c.altaDeviceId === selectedId);
    if (selectedCamera && !name) {
      setName(selectedCamera.altaDeviceName);
      if (selectedCamera.altaLocation && !location) {
        setLocation(selectedCamera.altaLocation);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!cameraId.trim()) {
      setError('Camera ID is required');
      return;
    }
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    // Phase 11: Removed captureStartHour/captureEndHour validation - now 24/7 operation

    try {
      const data: CreateCamera = {
        cameraId: cameraId.trim(),
        name: name.trim(),
        location: location.trim() || undefined,
        departmentId: departmentId || null,
        stationId: stationId || null,
        sopIds: selectedSopIds,
        captureEnabled,
        // Always use 30 minute intervals to avoid gaps in capture
        captureIntervalMinutes: 30,
        captureDurationMinutes: 30,
        // Phase 34 (F1): on create, leaving this at 1 omits the field so the
        // server defaults it from the Alta registry (D6); explicit multi-lens
        // picks and all edit-mode saves win.
        sourceCount: !isEdit && sourceCount === 1 ? undefined : sourceCount,
        // Phase 11: Removed captureStartHour/captureEndHour - now 24/7 operation
        altaDeploymentName: altaDeploymentName.trim() || undefined,
      };

      if (isEdit && camera) {
        await updateMutation.mutateAsync({ id: camera.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Phase 11: Removed hourOptions - now 24/7 operation

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-mint/50">
              <Camera size={20} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Camera' : 'Add Camera'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update camera configuration' : 'Configure a new camera'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {error && (
              <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
                <div className="h-2 w-2 rounded-full bg-red-500" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-5">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                <Camera size={14} />
                Camera Information
              </h3>

              {/* Camera ID */}
              <div>
                <label htmlFor="cameraId" className="block text-sm font-semibold text-gray-700 mb-2">
                  Camera <span className="text-red-500">*</span>
                </label>

                {isEdit ? (
                  // Show read-only display for edit mode
                  <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-mono text-gray-500">
                    {cameraId}
                  </div>
                ) : camerasLoading ? (
                  // Loading state
                  <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm flex items-center gap-2 text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    Loading available cameras...
                  </div>
                ) : (availableCameras && availableCameras.length > 0 && !useManualInput) ? (
                  // Dropdown for available cameras
                  <select
                    id="cameraId"
                    value={cameraId}
                    onChange={(e) => handleCameraSelect(e.target.value)}
                    disabled={isPending}
                    className="
                      w-full rounded-xl border border-gray-200 bg-white
                      px-4 py-3 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Select a camera...</option>
                    {availableCameras.map((cam) => (
                      <option key={cam.altaDeviceId} value={cam.altaDeviceId}>
                        {cam.altaDeviceName}
                        {cam.altaLocation && ` (${cam.altaLocation})`}
                        {cam.assignedOrgName && ` — ${cam.assignedOrgName}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Fallback to text input
                  <input
                    id="cameraId"
                    type="text"
                    value={cameraId}
                    onChange={(e) => setCameraId(e.target.value)}
                    placeholder="Enter Camera Device ID"
                    disabled={isPending}
                    className="
                      w-full rounded-xl border border-gray-200 bg-white
                      px-4 py-3 text-sm font-mono
                      placeholder:text-gray-400
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  />
                )}

                {/* Fallback toggle and help text */}
                {!isEdit && !camerasLoading && (
                  <div className="mt-1.5 flex items-center justify-between">
                    {(availableCameras && availableCameras.length > 0) ? (
                      <button
                        type="button"
                        onClick={() => setUseManualInput(!useManualInput)}
                        className="text-xs text-guild-green hover:underline"
                      >
                        {useManualInput ? 'Use camera dropdown' : 'Enter ID manually'}
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-amber-600">
                        <AlertCircle size={12} />
                        <span>No cameras available. Contact your super admin to assign cameras.</span>
                      </div>
                    )}
                  </div>
                )}

                {isEdit && (
                  <p className="mt-1.5 text-xs text-gray-500">Camera ID cannot be changed after creation</p>
                )}
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Assembly Line A - Main View"
                  disabled={isPending}
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                />
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                  Location
                  <span className="font-normal text-gray-400 ml-1">(optional)</span>
                </label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Building 3, Floor 2"
                  disabled={isPending}
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                />
              </div>

              {/* Video Sources (Phase 34: multi-sensor lens count) */}
              <div>
                <label htmlFor="sourceCount" className="block text-sm font-semibold text-gray-700 mb-2">
                  Video sources (lenses)
                </label>
                <select
                  id="sourceCount"
                  value={sourceCount}
                  onChange={(e) => setSourceCount(parseInt(e.target.value))}
                  disabled={isPending}
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  Multi-sensor models (e.g. H5A-3MH/4MH) record one stream per lens. Auto-detected from Alta when left at 1 on create.
                </p>
              </div>

              {/* Department & Zone row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Department */}
                <div>
                  <label htmlFor="departmentId" className="block text-sm font-semibold text-gray-700 mb-2">
                    Department
                    <span className="font-normal text-gray-400 ml-1">(optional)</span>
                  </label>
                  <select
                    id="departmentId"
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    disabled={isPending}
                    className="
                      w-full rounded-xl border border-gray-200 bg-white
                      px-4 py-3 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Select department</option>
                    {departments?.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Zone */}
                <div>
                  <label htmlFor="stationId" className="block text-sm font-semibold text-gray-700 mb-2">
                    Zone
                    <span className="font-normal text-gray-400 ml-1">(optional)</span>
                  </label>
                  <select
                    id="stationId"
                    value={stationId}
                    onChange={(e) => setStationId(e.target.value)}
                    disabled={isPending}
                    className="
                      w-full rounded-xl border border-gray-200 bg-white
                      px-4 py-3 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Select zone</option>
                    {stations?.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.name}
                        {station.location && ` (${station.location})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SOPs */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SOPs
                  <span className="font-normal text-gray-400 ml-1">(select all that apply)</span>
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-white">
                  {sops && sops.length > 0 ? (
                    sops.map((sop) => (
                      <label key={sop.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSopIds.includes(sop.id)}
                          onChange={() => {
                            setSelectedSopIds(prev =>
                              prev.includes(sop.id)
                                ? prev.filter(id => id !== sop.id)
                                : [...prev, sop.id]
                            );
                          }}
                          disabled={isPending}
                          className="rounded border-gray-300 text-guild-green focus:ring-guild-green"
                        />
                        <span className="text-sm text-gray-700">{sop.title}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 py-2 text-center">No SOPs available</p>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  The AI will automatically detect which SOP the operator is following from the video
                </p>
              </div>
            </div>

            {/* Capture Schedule */}
            <div className="space-y-5 pt-2">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                <Settings2 size={14} />
                Capture Schedule
              </h3>

              {/* Capture Enabled Toggle */}
              <label className="flex items-center justify-between py-3 px-4 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
                <div>
                  <span className="text-sm font-semibold text-gray-900">Enable Capture</span>
                  <p className="text-xs text-gray-500 mt-0.5">Automatically capture video from this camera</p>
                </div>
                <div className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                  ${captureEnabled ? 'bg-guild-green' : 'bg-gray-300'}
                `}>
                  <span className={`
                    inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200
                    ${captureEnabled ? 'translate-x-6' : 'translate-x-1'}
                  `} />
                  <input
                    type="checkbox"
                    checked={captureEnabled}
                    onChange={(e) => setCaptureEnabled(e.target.checked)}
                    disabled={isPending}
                    className="sr-only"
                  />
                </div>
              </label>

              {/* Phase 11: Removed time range selectors - now 24/7 operation */}

              {/* Capture Info Banner */}
              <div className="flex items-center gap-3 rounded-xl bg-guild-mint/30 border border-guild-green/20 px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-guild-green/10">
                  <Camera size={16} className="text-guild-green" />
                </div>
                <p className="text-sm text-gray-700">
                  Captures run <span className="font-semibold">24/7</span> in <span className="font-semibold">30-minute</span> segments when enabled.
                </p>
              </div>

              {/* Alta Deployment Override */}
              <div>
                <label htmlFor="altaDeployment" className="block text-sm font-semibold text-gray-700 mb-2">
                  Deployment Name
                  <span className="font-normal text-gray-400 ml-1">(optional override)</span>
                </label>
                <input
                  id="altaDeployment"
                  type="text"
                  value={altaDeploymentName}
                  onChange={(e) => setAltaDeploymentName(e.target.value)}
                  placeholder="Leave blank to use default deployment"
                  disabled={isPending}
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50/50 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-200
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && <Loader2 size={16} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Add Camera'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
